import {makeStyles} from "@material-ui/core"
import {ModernTheme} from "../../../../../theme/modern/tokens"
import {COARSE, touchField, touchTarget} from "../../../../../theme/modern/touchTargets"

export default makeStyles((theme: ModernTheme) => ({
    card: {
        background: theme.modern.panel,
        border: `1px solid ${theme.modern.line}`,
        borderRadius: theme.modern.radius,
        boxShadow: theme.modern.shadow,
        overflow: "hidden",
    },
    card_head: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "11px 14px",
        borderBottom: `1px solid ${theme.modern.line2}`,
        flexWrap: "wrap",
    },
    card_title: {
        fontSize: 14,
        fontWeight: 700,
        marginRight: 4,
        margin: 0,
        color: theme.modern.ink,
    },

    // ---- tabs -------------------------------------------------------------
    tabs: {
        display: "flex",
        gap: "2px",
        background: theme.modern.panel2,
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 7,
        padding: 2,
    },
    tab_btn: {
        padding: "4px 12px",
        borderRadius: 5,
        fontFamily: theme.modern.sans,
        fontWeight: 500,
        fontSize: 12,
        color: theme.modern.ink2,
        whiteSpace: "nowrap",
        border: "none",
        background: "none",
        cursor: "pointer",

        "&:hover": {color: theme.modern.ink},

        [COARSE]: touchTarget({padding: "0 16px"}),
    },
    tab_btn__active: {
        background: theme.modern.panel,
        color: theme.modern.ink,
        fontWeight: 500,
        boxShadow: theme.modern.shadowSegment,
    },

    // ---- search -----------------------------------------------------------
    search: {
        position: "relative",
        marginLeft: "auto",

        "@media (max-width:640px)": {marginLeft: 0, width: "100%"},
    },
    search_input: {
        width: 230,
        height: 30,
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 7,
        background: theme.modern.panel2,
        color: theme.modern.ink,
        font: `400 12px ${theme.modern.sans}`,
        padding: "0 12px 0 30px",

        "&:focus": {outline: `2px solid ${theme.modern.accent}`, outlineOffset: -1},
        "&::placeholder": {color: theme.modern.ink3},

        "@media (max-width:640px)": {width: "100%"},

        [COARSE]: touchField(),
    },
    search_icon: {
        position: "absolute",
        left: 9,
        // centred by transform rather than by a hard-coded 8 px offset, which
        // was only correct while the field was exactly 30 px tall — it grows to
        // 44 under COARSE and the icon has to follow
        top: "50%",
        transform: "translateY(-50%)",
        width: 14,
        height: 14,
        color: theme.modern.ink3,
        pointerEvents: "none",
    },

    // ---- table ------------------------------------------------------------
    newbar: {
        display: "flex",
        justifyContent: "center",
        padding: "6px 14px",
        background: theme.modern.accentSoft,
        borderBottom: `1px solid ${theme.modern.line2}`,
    },
    newbtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: "7px",
        font: `500 12px ${theme.modern.sans}`,
        color: theme.modern.accent,
        background: theme.modern.panel,
        border: `1px solid ${theme.modern.accentLine}`,
        borderRadius: 999,
        padding: "4px 14px",
        cursor: "pointer",
        textTransform: "none",

        "&:hover": {boxShadow: theme.modern.shadowHover},
    },
    newbtn_icon: {
        width: 12,
        height: 12,
        flex: "none",
        transform: "rotate(90deg)",
    },
    tablewrap: {
        overflowX: "auto",
    },
    table: {
        width: "100%",
        borderCollapse: "separate",
        borderSpacing: 0,
        minWidth: 780,

        "& thead th": {
            background: theme.modern.panel2,
            textAlign: "left",
            font: `600 12px/1 ${theme.modern.sans}`,
            color: theme.modern.ink2,
            padding: "10px 12px",
            borderBottom: `1px solid ${theme.modern.line}`,
            whiteSpace: "nowrap",
            userSelect: "none",
        },
        "& tbody td": {
            padding: "9px 12px",
            borderBottom: `1px solid ${theme.modern.line2}`,
            verticalAlign: "middle",
            whiteSpace: "nowrap",
        },
        "& tbody tr:last-child td": {borderBottom: "none"},
        "& tbody tr": {transition: "background .1s", cursor: "default"},
        "& tbody tr:hover": {background: theme.modern.panel2},
        /**
         * Keyboard parity with the hover rule above.
         *
         * Rows are opened by double-click, which leaves hover as the only signal
         * that a row is more than text — a signal neither a keyboard nor a
         * finger ever receives. Tabbing to the copy or open button inside a row
         * now lights the whole row, so the reviewer can see which record the
         * focused control belongs to.
         */
        "& tbody tr:focus-within": {background: theme.modern.panel2},
        // attribute selector outranks the `& thead th` rule above, which is how
        // the Action header gets right-aligned without an !important
        "& thead th[data-align=\"right\"]": {textAlign: "right"},
        "& thead th[data-sortable=\"true\"]": {cursor: "pointer"},
        "& thead th[data-sortable=\"true\"]:hover": {color: theme.modern.ink},
        // sorting is a tap with no other confirmation until the rows reorder
        "& thead th[data-sortable=\"true\"]:active": {background: theme.modern.line2},

        // roomier rows so neighbouring records are harder to hit by mistake
        [COARSE]: {
            "& thead th": {padding: "14px 12px"},
            "& tbody td": {padding: "12px"},
        },
    },
    sorticon: {
        display: "inline-flex",
        alignItems: "center",
        gap: "1px",
        fontFamily: theme.modern.mono,
        fontSize: 9,
        marginLeft: 5,
        color: theme.modern.ink3,
        verticalAlign: "middle",
    },
    sorticon__active: {
        color: theme.modern.accent,
    },
    sortfunnel: {
        width: 12,
        height: 12,
        flex: "none",
    },

    // ---- cells ------------------------------------------------------------
    td_dt: {
        font: `500 12px ${theme.modern.mono}`,
        color: theme.modern.ink,
    },
    res: {
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        font: `700 9.5px ${theme.modern.sans}`,
        letterSpacing: ".07em",
        padding: "3px 10px",
        borderRadius: 999,
        border: "1px solid transparent",
    },
    res__ok: {
        background: theme.modern.okSoft,
        color: theme.modern.ok,
        borderColor: theme.modern.resOkBorder,
    },
    res__warn: {
        background: theme.modern.warnSoft,
        color: theme.modern.warn,
        borderColor: theme.modern.resWarnBorder,
    },
    res_text: {
        maxWidth: 200,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    dot: {
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: "currentColor",
        flex: "none",
    },
    cellico: {
        width: 15,
        height: 15,
        verticalAlign: -3,
        marginRight: 7,
        color: theme.modern.ink3,
        flex: "none",
    },
    td_rec: {
        font: `500 12px ${theme.modern.mono}`,
        color: theme.modern.ink,
    },
    copy: {
        display: "inline-grid",
        placeItems: "center",
        width: 22,
        height: 22,
        borderRadius: 5,
        marginLeft: 6,
        color: theme.modern.accent,
        verticalAlign: -6,
        border: "none",
        background: "none",
        cursor: "pointer",

        "&:hover": {background: theme.modern.accentSoft},
        "& svg": {width: 13, height: 13},
        // touch has no hover, so the tap needs its own acknowledgement — this is
        // the control that copies a record ID, and a silent tap reads as a miss
        "&:active": {background: theme.modern.accentLine},

        // 22 x 22 is half the touch floor. The 13 px glyph is untouched; only
        // the box around it grows.
        [COARSE]: {width: 44, height: 44, marginLeft: 2, verticalAlign: "middle"},
    },
    copy__done: {
        color: theme.modern.ok,
    },
    td_cc: {
        font: `500 12px ${theme.modern.mono}`,
        color: theme.modern.ink,
    },
    td_pt: {
        font: `500 12.5px ${theme.modern.mono}`,
        color: theme.modern.ink,
        whiteSpace: "nowrap",
    },
    td_hr: {
        whiteSpace: "nowrap",

        "& b": {font: `600 13px ${theme.modern.mono}`, color: theme.modern.ink},
        "& small": {font: `500 10.5px ${theme.modern.mono}`, color: theme.modern.ink3, marginLeft: 3},
    },
    heart: {
        width: 15,
        height: 15,
        verticalAlign: -3,
        marginRight: 7,
        flex: "none",
    },
    heart__good: {
        color: theme.modern.ok,
    },
    heart__bad: {
        color: theme.modern.crit,
    },
    td_act: {
        textAlign: "right",
    },
    open: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        height: 29,
        padding: "0 12px",
        borderRadius: 7,
        border: `1px solid ${theme.modern.accentLine}`,
        background: theme.modern.panel,
        font: `600 11.5px ${theme.modern.sans}`,
        color: theme.modern.accent,
        cursor: "pointer",

        "&:hover": {background: theme.modern.accentSoft},
        "& svg": {width: 13, height: 13, flex: "none"},
        "&:active:not(:disabled)": {background: theme.modern.accentLine},

        "&:disabled": {
            opacity: .45,
            cursor: "not-allowed",
            background: theme.modern.panel,
        },

        [COARSE]: touchTarget({height: "auto", padding: "0 18px"}),
    },
    open__primary: {
        background: theme.modern.accent,
        borderColor: theme.modern.accent,
        color: theme.modern.onAccent,

        "&:hover": {filter: "brightness(1.07)", background: theme.modern.accent},
        "&:disabled": {background: theme.modern.accent},
    },

    // ---- empty / loading / foot -------------------------------------------
    empty: {
        padding: 34,
        textAlign: "center",
        color: theme.modern.ink2,
    },
    empty_title: {
        display: "block",
        fontSize: 14,
        color: theme.modern.ink,
        marginBottom: 4,
        fontWeight: 700,
    },
    empty_inline_btn: {
        display: "inline-flex",
        marginLeft: 6,
    },
    loadmore: {
        padding: 12,
        textAlign: "center",
        font: `500 11px ${theme.modern.mono}`,
        color: theme.modern.ink3,
    },
    sentinel: {
        height: 1,
    },
    card_foot: {
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "10px 14px",
        borderTop: `1px solid ${theme.modern.line2}`,
        flexWrap: "wrap",
    },
    count: {
        font: `500 11px ${theme.modern.mono}`,
        color: theme.modern.ink3,
    },
    live_region: {
        position: "absolute",
        width: 1,
        height: 1,
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
        whiteSpace: "nowrap",
    },
}))
